import { useState } from 'react';
import {
  useGetChronumPayrollOverviewQuery,
  useSyncChronumPayrollMutation,
  useEnqueuePayrollBatchToPaymentMutation,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconX } from '@shared/ui/icons';
import { useAgentPermission } from '@entities/user';
import { formatMillions } from '../financierFormat';
import {
  Container,
  HeaderRow,
  TitleBox,
  Title,
  Subtitle,
  TabNav,
  TabButton,
  LoadingCard,
  ErrorCard,
  AlertBanner,
} from './styled';
import { PayrollKpiGrid } from './PayrollKpiGrid';
import { PayrollBatchesTable } from './PayrollBatchesTable';
import { PayrollSitesTable } from './PayrollSitesTable';

export function PayrollView() {
  const { can_write: canWrite } = useAgentPermission('finance');
  const { data, isLoading, isError, refetch } = useGetChronumPayrollOverviewQuery();
  const [syncChronum, { isLoading: isSyncing }] = useSyncChronumPayrollMutation();
  const [enqueueBatch, { isLoading: isEnqueuing }] = useEnqueuePayrollBatchToPaymentMutation();

  const [activeTab, setActiveTab] = useState<'batches' | 'sites'>('batches');
  const [banner, setBanner] = useState<{ text: string; tone: 'success' | 'warning' } | null>(null);

  if (isLoading) {
    return <LoadingCard>Загрузка зарплатного шлюза ПК «Хронум»…</LoadingCard>;
  }

  if (isError || !data) {
    return (
      <ErrorCard>
        <span>Не удалось загрузить данные зарплатного шлюза ПК «Хронум».</span>
        <Button $variant="secondary" $size="sm" onClick={() => refetch()}>
          Повторить
        </Button>
      </ErrorCard>
    );
  }

  const { totals, batches, site_accruals: sites } = data;

  async function handleSync() {
    try {
      const res = await syncChronum().unwrap();
      setBanner({
        text: `Синхронизация завершена (${res.is_live_external ? 'Live API терминалов' : 'Локальный расчёт'}): обновлено объектов: ${res.sites_updated_count}, сотрудников: ${res.total_workers}, ФОТ: ${formatMillions(res.total_fot_rub)}.`,
        tone: 'success',
      });
      refetch();
    } catch {
      setBanner({
        text: 'Не удалось выполнить синхронизацию с ПК Хронум.',
        tone: 'warning',
      });
    }
  }

  async function handleEnqueue(batchId: string) {
    try {
      await enqueueBatch({ batch_id: batchId }).unwrap();
      setBanner({
        text: 'Зарплатная ведомость успешно включена в очередь фабрики платежей на акцепт Гендиректора.',
        tone: 'success',
      });
      refetch();
    } catch {
      setBanner({
        text: 'Ошибка при передаче зарплатной ведомости в фабрику платежей.',
        tone: 'warning',
      });
    }
  }

  return (
    <Container>
      <HeaderRow>
        <TitleBox>
          <Title>Шлюз ПК «Хронум»: УРВ, табель и зарплатные ведомости</Title>
          <Subtitle>
            Биометрический учет рабочего времени, пообъектная сверка с рапортами прорабов и выплата ФОТ
          </Subtitle>
        </TitleBox>

        {canWrite && (
          <Button
            $variant="primary"
            $size="sm"
            onClick={handleSync}
            disabled={isSyncing}
          >
            {isSyncing ? 'Синхронизация…' : 'Синхронизировать с ПК Хронум'}
          </Button>
        )}
      </HeaderRow>

      {banner && (
        <AlertBanner $tone={banner.tone}>
          <span>{banner.text}</span>
          <Button $variant="ghost" $size="sm" onClick={() => setBanner(null)} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </AlertBanner>
      )}

      {/* KPI карточки */}
      <PayrollKpiGrid totals={totals} />

      {/* Переключение вкладок */}
      <TabNav>
        <TabButton
          $active={activeTab === 'batches'}
          onClick={() => setActiveTab('batches')}
        >
          Зарплатные ведомости к выплате ({batches.length})
        </TabButton>
        <TabButton
          $active={activeTab === 'sites'}
          onClick={() => setActiveTab('sites')}
        >
          Детализация по объектам строительства ({sites.length})
        </TabButton>
      </TabNav>

      {/* Вкладка 1: Ведомости к выплате */}
      {activeTab === 'batches' && (
        <PayrollBatchesTable
          batches={batches}
          onEnqueue={handleEnqueue}
          isEnqueuing={isEnqueuing}
          canWrite={Boolean(canWrite)}
        />
      )}

      {/* Вкладка 2: Детализация по строительным объектам */}
      {activeTab === 'sites' && (
        <PayrollSitesTable sites={sites} />
      )}
    </Container>
  );
}
